
import { v4 as uuidv4 } from "uuid";
import { 
  Character, 
  CharacterAction, 
  CharacterSheetInterface, 
  BasicCreatureInstance, 
  FeatureAncestry, 
  SummonInstance 
} from "@dndModels";
import { AbilityScoreExpandedType, DataType } from "@dndModels/Types";

export class UpgradableNumber {
  base = 0; // the base number
  add_char_level_mult = 0; // add a multiple of the character level
  add_source_level_mult = 0; // multiply the class level by this when adding
  add_spell_save_dc_mult = 0;
  add_spell_attack_mult = 0;
  add_non_magic_attack_mult = 0;
  add_magic_dmg_bonus_mult = 0;
  add_non_magic_dmg_bonus_mult = 0;
  add_prof_bonus_mult = 0;
  add_slot_mult = 0;
  add_upcast_mult = 0;

  // add_ability_scores: AbilityScoreExpandedType[] = [];
  // add_ability_score_mult = 0;
  // add_ability_mod_mult = 0;
  // multiple_type: "Or" | "And" = "And";
  add_stats_groups: AddStatsUGN[] = [];

  add_hp_mult = 0;
  add_walking_speed_mult = 0;
  min: number | null = null;
  max: number | null = null;
  add_made_with_slot_level_mult = 0;
  // These don't currently have multipliers, but I may need to add them later
  source_type: DataType | "" = "";
  source_id = "";
  spellcasting_class_id = "";
  table_type: DataType | "Attached" | "" = "";
  table_id = "";
  table_field = "";
  formula_key = ""; // FeatureSources can have a hash of formulae which are upgradable numbers.  Other upgradable numbers can reference these.  Their values are stored on Character, and can be affected by Modifiers.
  resource_dice_size = "";
  resource_dice_count = "";
  item_rarity_mult = 0;
  selected_rarity_mult = 0;
  max_slot_for = ""; // Any or class_id
  rounding: "Up" | "Down" | "Standard" = "Down";
  selectable_key = "";
  
  constructor(obj?: any, log_it?: boolean) {
    if (obj) {
      if (typeof(obj) === "object") {
        this.base = obj.base ? obj.base : 0;
        this.add_char_level_mult = obj.add_char_level_mult ? obj.add_char_level_mult : 0;
        this.add_source_level_mult = obj.add_source_level_mult ? obj.add_source_level_mult : (obj.add_class_level_mult ? obj.add_class_level_mult : 0);
        
        this.add_spell_save_dc_mult = obj.add_spell_save_dc_mult ? obj.add_spell_save_dc_mult : 0;
        this.add_spell_attack_mult = obj.add_spell_attack_mult ? obj.add_spell_attack_mult : 0;
        this.add_non_magic_attack_mult = obj.add_non_magic_attack_mult ? obj.add_non_magic_attack_mult : 0;
        this.add_magic_dmg_bonus_mult = obj.add_magic_dmg_bonus_mult ? obj.add_magic_dmg_bonus_mult : 0;
        this.add_non_magic_dmg_bonus_mult = obj.add_non_magic_dmg_bonus_mult ? obj.add_non_magic_dmg_bonus_mult : 0;

        this.add_prof_bonus_mult = obj.add_prof_bonus_mult ? obj.add_prof_bonus_mult : 0;
        this.add_slot_mult = obj.add_slot_mult ? obj.add_slot_mult : 0;
        this.add_upcast_mult = obj.add_upcast_mult ? obj.add_upcast_mult : (obj.add_slot_level_mult ? obj.add_slot_level_mult : 0);
        
        let add_ability_scores: AbilityScoreExpandedType[] = [];
        if (obj.add_ability_scores) {
          add_ability_scores = obj.add_ability_scores;
        } else if (obj.add_ability_score && obj.add_ability_scores !== "None") {
          add_ability_scores = [obj.add_ability_score];
        }
        
        this.add_stats_groups = obj.add_stats_groups ? obj.add_stats_groups : [];
        if (!obj.add_stats_groups && add_ability_scores.length > 0) {
          // Convert it to new format.
          const add_ability_score_mult = obj.add_ability_score_mult ? obj.add_ability_score_mult : 0;
          const add_ability_mod_mult = obj.add_ability_mod_mult ? obj.add_ability_mod_mult : 0;
          const mod_or_score = add_ability_mod_mult !== 0 ? "Mod" : (add_ability_score_mult !== 0 ? "Score" : "Mod");
          const add_mult = add_ability_mod_mult !== 0 ? add_ability_mod_mult : (add_ability_score_mult !== 0 ? add_ability_score_mult : add_ability_mod_mult);
          const multiple_type = obj.multiple_type ? obj.multiple_type : obj.ability_scores_type ? obj.ability_scores_type : "And";
          const group: AddStatsUGN = {
            true_id: uuidv4().toString(),
            add_ability_scores,
            add_mult,
            mod_or_score,
            multiple_type
          };
          this.add_stats_groups.push(group);
        }
        this.add_hp_mult = obj.add_hp_mult ? obj.add_hp_mult : 0;
        this.add_walking_speed_mult = obj.add_walking_speed_mult ? obj.add_walking_speed_mult : 0;
        this.min = obj.min ? obj.min : null;
        this.table_type = obj.table_type ? obj.table_type : "";
        this.table_id = obj.table_id ? obj.table_id : "";
        if (this.table_type === "Attached") {
          this.table_id = "Attached";
        }
        this.source_id = obj.source_id ? obj.source_id : (obj.class_id ? obj.class_id : "");
        this.source_type = obj.source_type ? obj.source_type : (this.source_id !== "" ? "game_class" : "");
        this.spellcasting_class_id = obj.spellcasting_class_id ? obj.spellcasting_class_id : "";
        if (this.spellcasting_class_id === "" && this.source_id && !obj.source_type) {
          this.spellcasting_class_id = this.source_id;
        }
        this.table_field = obj.table_field ? obj.table_field : "";
        this.formula_key = obj.formula_key ? obj.formula_key : "";
        this.resource_dice_size = obj.resource_dice_size ? obj.resource_dice_size : "";
        this.resource_dice_count = obj.resource_dice_count ? obj.resource_dice_count : "";
        this.max = obj.max ? obj.max : null;
        this.rounding = obj.rounding ? obj.rounding : "Down";
        this.item_rarity_mult = obj.item_rarity_mult ? obj.item_rarity_mult : 0;
        this.selected_rarity_mult = obj.selected_rarity_mult ? obj.selected_rarity_mult : 0;
        this.add_made_with_slot_level_mult = obj.add_made_with_slot_level_mult ? obj.add_made_with_slot_level_mult : 0;
        this.max_slot_for = obj.max_slot_for ? obj.max_slot_for : "";
        this.selectable_key = obj.selectable_key ? obj.selectable_key : "";
      } else {
        this.base = +obj;
      }
    }
  }

  toDBObj(): any {
    return {
      base: this.base,
      add_char_level_mult: this.add_char_level_mult,
      add_source_level_mult: this.add_source_level_mult,
      add_spell_save_dc_mult: this.add_spell_save_dc_mult,
      add_spell_attack_mult: this.add_spell_attack_mult,
      add_non_magic_attack_mult: this.add_non_magic_attack_mult,
      add_magic_dmg_bonus_mult: this.add_magic_dmg_bonus_mult,
      add_non_magic_dmg_bonus_mult: this.add_non_magic_dmg_bonus_mult,
      add_prof_bonus_mult: this.add_prof_bonus_mult,
      add_slot_mult: this.add_slot_mult,
      // add_ability_scores: this.add_ability_scores,
      // add_ability_score_mult: this.add_ability_score_mult,
      // add_ability_mod_mult: this.add_ability_mod_mult,
      // multiple_type: this.multiple_type,
      add_hp_mult: this.add_hp_mult,
      add_stats_groups: this.add_stats_groups,
      add_walking_speed_mult: this.add_walking_speed_mult,
      min: this.min,
      max: this.max,
      rounding: this.rounding,
      table_type: this.table_type,
      table_id: this.table_id,
      source_type: this.source_type,
      source_id: this.source_id,
      spellcasting_class_id: this.spellcasting_class_id,
      table_field: this.table_field,
      formula_key: this.formula_key,
      resource_dice_size: this.resource_dice_size,
      resource_dice_count: this.resource_dice_count,
      item_rarity_mult: this.item_rarity_mult,
      selected_rarity_mult: this.selected_rarity_mult,
      add_made_with_slot_level_mult: this.add_made_with_slot_level_mult,
      max_slot_for: this.max_slot_for,
      selectable_key: this.selectable_key,
    };
  }

  copy(copyMe: UpgradableNumber): void {
    this.base = copyMe.base;
    this.add_char_level_mult = copyMe.add_char_level_mult;
    this.add_source_level_mult = copyMe.add_source_level_mult;
    this.add_spell_save_dc_mult = copyMe.add_spell_save_dc_mult;
    this.add_spell_attack_mult = copyMe.add_spell_attack_mult;
    this.add_non_magic_attack_mult = copyMe.add_non_magic_attack_mult;
    this.add_magic_dmg_bonus_mult = copyMe.add_magic_dmg_bonus_mult;
    this.add_non_magic_dmg_bonus_mult = copyMe.add_non_magic_dmg_bonus_mult;
    this.add_prof_bonus_mult = copyMe.add_prof_bonus_mult;
    this.add_slot_mult = copyMe.add_slot_mult;
    // this.add_ability_scores = copyMe.add_ability_scores;
    // this.add_ability_score_mult = copyMe.add_ability_score_mult;
    // this.add_ability_mod_mult = copyMe.add_ability_mod_mult;
    // // if (this.multiple_type === "Or" || copyMe.multiple_type === "Or") {
    // //   debugger;
    // // }
    // this.multiple_type = copyMe.multiple_type;
    this.add_stats_groups = [ ...copyMe.add_stats_groups ];
    this.add_hp_mult = copyMe.add_hp_mult;
    this.add_walking_speed_mult = copyMe.add_walking_speed_mult;
    this.min = copyMe.min;
    this.max = copyMe.max;
    this.rounding = copyMe.rounding;
    this.table_type = copyMe.table_type;
    this.table_id = copyMe.table_id;
    this.source_type = copyMe.source_type;
    this.source_id = copyMe.source_id;
    this.spellcasting_class_id = copyMe.spellcasting_class_id;
    this.table_field = copyMe.table_field;
    this.formula_key = copyMe.formula_key;
    this.resource_dice_size = copyMe.resource_dice_size;
    this.resource_dice_count = copyMe.resource_dice_count;
    this.item_rarity_mult = copyMe.item_rarity_mult;
    this.selected_rarity_mult = copyMe.selected_rarity_mult;
    this.add_made_with_slot_level_mult = copyMe.add_made_with_slot_level_mult;
    this.max_slot_for = copyMe.max_slot_for;
    this.selectable_key = copyMe.selectable_key;
  }

  clone(): UpgradableNumber {
    const new_obj = new UpgradableNumber();
    new_obj.copy(this);
    return new_obj;
  }

  isBlank = () => {
    return (this.base === 0 &&
      this.add_char_level_mult === 0 &&
      this.add_source_level_mult === 0 &&
      this.add_spell_save_dc_mult === 0 &&
      this.add_spell_attack_mult === 0 &&
      this.add_non_magic_attack_mult === 0 &&
      this.add_magic_dmg_bonus_mult === 0 &&
      this.add_non_magic_dmg_bonus_mult === 0 &&
      this.add_prof_bonus_mult === 0 &&
      this.add_slot_mult === 0 &&
      this.add_upcast_mult === 0 &&
      // this.add_ability_scores.length === 0 &&
      // this.add_ability_score_mult === 0 &&
      // this.add_ability_mod_mult === 0 &&
      // this.multiple_type === "And" &&
      this.add_stats_groups.length === 0 &&
      this.add_hp_mult === 0 &&
      this.add_walking_speed_mult === 0 &&
      this.min === null &&
      this.max === null &&
      this.rounding === "Down" &&
      this.formula_key === "" &&
      this.table_type === "" &&
      this.table_id === "" &&
      this.source_type === "" &&
      this.source_id === "" &&
      this.spellcasting_class_id === "" &&
      this.table_field === "" &&
      this.item_rarity_mult === 0 &&
      this.selected_rarity_mult === 0 && 
      this.add_made_with_slot_level_mult === 0 &&
      this.max_slot_for === "" && 
      this.selectable_key === ""
    );
  }

  isBasic = () => {
    return (this.add_char_level_mult === 0 &&
      this.add_source_level_mult === 0 &&
      this.add_spell_save_dc_mult === 0 &&
      this.add_spell_attack_mult === 0 &&
      this.add_non_magic_attack_mult === 0 &&
      this.add_magic_dmg_bonus_mult === 0 &&
      this.add_non_magic_dmg_bonus_mult === 0 &&
      this.add_prof_bonus_mult === 0 &&
      this.add_slot_mult === 0 &&
      this.add_upcast_mult === 0 &&
      // this.add_ability_scores.length === 0 &&
      // this.add_ability_score_mult === 0 &&
      // this.add_ability_mod_mult === 0 &&
      // this.multiple_type === "And" &&
      this.add_stats_groups.length === 0 &&
      this.add_hp_mult === 0 &&
      this.add_walking_speed_mult === 0 &&
      this.min === null &&
      this.max === null &&
      this.rounding === "Down" &&
      this.table_type === "" &&
      this.table_id === "" &&
      this.source_type === "" &&
      this.source_id === "" && 
      this.spellcasting_class_id === "" &&
      this.table_field === "" &&
      this.formula_key === "" &&
      this.resource_dice_count === "" &&
      this.resource_dice_size === "" &&
      this.item_rarity_mult === 0 &&
      this.selected_rarity_mult === 0 &&
      this.add_made_with_slot_level_mult === 0 &&
      this.max_slot_for === "" &&
      this.selectable_key === ""
    );
  }

  isBasicExcept = (fields: string[]) => {
    return ((fields.includes("add_char_level_mult") || this.add_char_level_mult === 0) &&
      (fields.includes("add_source_level_mult") || this.add_source_level_mult === 0) &&
      (fields.includes("add_spell_save_dc_mult") || this.add_spell_save_dc_mult === 0) &&
      (fields.includes("add_spell_attack_mult") || this.add_spell_attack_mult === 0) &&
      (fields.includes("add_non_magic_attack_mult") || this.add_non_magic_attack_mult === 0) &&
      (fields.includes("add_magic_dmg_bonus_mult") || this.add_magic_dmg_bonus_mult === 0) &&
      (fields.includes("add_non_magic_dmg_bonus_mult") || this.add_non_magic_dmg_bonus_mult === 0) &&
      (fields.includes("add_prof_bonus_mult") || this.add_prof_bonus_mult === 0) &&
      (fields.includes("add_slot_mult") || this.add_slot_mult === 0) &&
      (fields.includes("add_upcast_mult") || this.add_upcast_mult === 0) &&
      // (fields.includes("add_ability_scores") || this.add_ability_scores.length === 0) &&
      // (fields.includes("add_ability_score_mult") || this.add_ability_score_mult === 0) &&
      // (fields.includes("add_ability_mod_mult") || this.add_ability_mod_mult === 0) &&
      // (fields.includes("ability_scores_type") || this.multiple_type === "And") &&
      (fields.includes("add_hp_mult") || this.add_hp_mult === 0) &&
      (fields.includes("add_stats_hash") || this.add_stats_groups.length === 0) &&
      (fields.includes("add_walking_speed_mult") || this.add_walking_speed_mult === 0) &&
      (fields.includes("min") || this.min === null) &&
      (fields.includes("max") || this.max === null) &&
      (fields.includes("rounding") || this.rounding === "Down") &&
      (fields.includes("table_type") || this.table_type === "") &&
      (fields.includes("table_id") || this.table_id === "") &&
      (fields.includes("source_type") || this.source_type === "") &&
      (fields.includes("source_id") || this.source_id === "") && 
      (fields.includes("spellcasting_class_id") || this.spellcasting_class_id === "") &&
      (fields.includes("table_field") || this.table_field === "") &&
      (fields.includes("formula_key") || this.formula_key === "") &&
      (fields.includes("resource_dice_count") || this.resource_dice_count === "") &&
      (fields.includes("resource_dice_size") || this.resource_dice_size === "") &&
      (fields.includes("item_rarity_mult") || this.item_rarity_mult === 0) &&
      (fields.includes("selected_rarity_mult") || this.selected_rarity_mult === 0) &&
      (fields.includes("add_made_with_slot_level_mult") || this.add_made_with_slot_level_mult === 0) &&
      (fields.includes("max_slot_for") || this.max_slot_for === "") &&
      (fields.includes("selectable_key") || this.selectable_key === "")
    );
  }

  makeBasic = () => {
    this.add_char_level_mult = 0;
    this.add_source_level_mult = 0;
    this.add_spell_save_dc_mult = 0;
    this.add_spell_attack_mult = 0;
    this.add_non_magic_attack_mult = 0;
    this.add_magic_dmg_bonus_mult = 0;
    this.add_non_magic_dmg_bonus_mult = 0;
    this.add_prof_bonus_mult = 0;
    this.add_slot_mult = 0;
    this.add_upcast_mult = 0;
    // this.add_ability_scores = [];
    // this.add_ability_score_mult = 0;
    // this.add_ability_mod_mult = 0;
    // this.multiple_type = "And";
    this.add_stats_groups = [];
    this.add_hp_mult = 0;
    this.add_walking_speed_mult = 0;
    this.min = null;
    this.max = null;
    this.rounding = "Down";
    this.table_type = "";
    this.table_id = "";
    this.source_type = "";
    this.source_id = "";
    this.spellcasting_class_id = "";
    this.table_field = "";
    this.formula_key = "";
    this.resource_dice_count = "";
    this.resource_dice_size = "";
    this.item_rarity_mult = 0;
    this.selected_rarity_mult = 0;
    this.add_made_with_slot_level_mult = 0;
    this.max_slot_for = "";
    this.selectable_key = "";
  }

  merge = (mergeMe: UpgradableNumber) => {
    this.add_char_level_mult += mergeMe.add_char_level_mult;
    this.add_source_level_mult += mergeMe.add_source_level_mult;
    this.add_spell_save_dc_mult += mergeMe.add_spell_save_dc_mult;
    this.add_spell_attack_mult += mergeMe.add_spell_attack_mult;
    this.add_non_magic_attack_mult += mergeMe.add_non_magic_attack_mult;
    this.add_magic_dmg_bonus_mult += mergeMe.add_magic_dmg_bonus_mult;
    this.add_non_magic_dmg_bonus_mult += mergeMe.add_non_magic_dmg_bonus_mult;
    this.add_prof_bonus_mult += mergeMe.add_prof_bonus_mult;
    this.add_slot_mult += mergeMe.add_slot_mult;
    this.add_upcast_mult += mergeMe.add_upcast_mult;
    // this.add_ability_score_mult += mergeMe.add_ability_score_mult;
    // this.add_ability_mod_mult += mergeMe.add_ability_mod_mult;
    this.add_hp_mult += mergeMe.add_hp_mult;
    this.add_walking_speed_mult += mergeMe.add_walking_speed_mult;
    this.item_rarity_mult += mergeMe.item_rarity_mult;
    this.selected_rarity_mult += mergeMe.selected_rarity_mult;
    this.add_made_with_slot_level_mult += mergeMe.add_made_with_slot_level_mult;
    // Not Supported
    // this.add_ability_scores = [];
    // this.min = null;
    // this.table_type = "";
    // this.table_id = "";
    // this.table_field = "";
    // this.formula_key = "";
    // this.resource_dice_count = "";
    // this.resource_dice_size = "";
  }

  getFormula = () => {
    let str = `${this.base}`;
    if (this.source_type !== "" && this.source_id !== "") {
      str += ` (Using {${ this.source_type }=${ this.source_id }})`;
    }
    if (this.spellcasting_class_id !== "") {
      str += ` (Spellcasting Class {game_class=${ this.spellcasting_class_id }})`;
    }
    if (this.add_char_level_mult !== 0) {
      str += ` + ${ this.add_char_level_mult } * Char. Level`;
    }
    if (this.add_source_level_mult !== 0) {
      str += ` + ${ this.add_source_level_mult } * Source Level`;
    }
    if (this.add_made_with_slot_level_mult !== 0) {
      str += ` + ${ this.add_made_with_slot_level_mult } * Made with Slot Level`;
    }
    if (this.item_rarity_mult !== 0) {
      str += ` + ${ this.item_rarity_mult } * Item Rarity`;
    }
    if (this.selected_rarity_mult !== 0) {
      str += ` + ${ this.selected_rarity_mult } * Selected Rarity`;
    }
    if (this.add_spell_save_dc_mult !== 0) {
      str += ` + ${ this.add_spell_save_dc_mult } * DC`;
    }
    if (this.add_spell_attack_mult !== 0) {
      str += ` + ${ this.add_spell_attack_mult } * Spell Attack`;
    }
    if (this.add_non_magic_attack_mult !== 0) {
      str += ` + ${ this.add_non_magic_attack_mult } * Non Magic Attack`;
    }
    if (this.add_magic_dmg_bonus_mult !== 0) {
      str += ` + ${ this.add_magic_dmg_bonus_mult } * Magic DMG Bonus`;
    }
    if (this.add_non_magic_dmg_bonus_mult !== 0) {
      str += ` + ${ this.add_non_magic_dmg_bonus_mult } * Non Magic DMG Bonus`;
    }
    if (this.add_prof_bonus_mult !== 0) {
      str += ` + ${ this.add_prof_bonus_mult } * Prof. Bonus`;
    }
    // if (this.add_ability_mod_mult !== 0 || this.add_ability_score_mult !== 0) {
    //   let value = this.add_ability_mod_mult;
    //   let label = "Mod";
    //   if (this.add_ability_mod_mult === 0) {
    //     label = "Score";
    //     value = this.add_ability_score_mult;
    //   }
    //   if (this.multiple_type === "And") {
    //     str += ` + ${value} * (${ this.add_ability_scores.join(" + ") } ${label})`;
    //   } else {
    //     str += ` + ${value} * (Highest of ${ this.add_ability_scores.join(", ") } ${label})`;
    //   }
    // }
    if (this.add_stats_groups.length > 0) {
      for (const group of this.add_stats_groups) {
        const value = group.add_mult;
        const label = group.mod_or_score;
        if (group.multiple_type === "And") {
          str += ` + ${value} * (${ group.add_ability_scores.join(" + ") } ${label})`;
        } else {
          str += ` + ${value} * (Highest of ${ group.add_ability_scores.join(", ") } ${label})`;
        }
      }
    }
    if (this.add_hp_mult !== 0) {
      str += ` + ${ this.add_hp_mult } * Max HP`;
    }
    if (this.add_walking_speed_mult !== 0) {
      str += ` + ${ this.add_walking_speed_mult } * Walking Speed`;
    }
    if (this.add_upcast_mult !== 0) {
      str += ` + ${ this.add_upcast_mult } * Upcast`;
    }
    if (this.add_slot_mult !== 0) {
      str += ` + ${ this.add_slot_mult } * Slot Level`;
    }
    if (this.table_field !== "") {
      if (this.table_type === "Attached") {
        str += ` + Attached Table ${ this.table_field }`;
      } else {
        str += ` + {table=${ this.table_id }} ${ this.table_field }`;
      }
    }
    if (this.max_slot_for !== "") {
      if (this.max_slot_for === "Any") {
        str += ` + Max Slot Size`;
      } else {
        str += ` + Max Slot Size for {game_class=${this.max_slot_for}}`;
      }
    }
    if (this.formula_key !== "") {
      str += ` + ${ this.formula_key }`;
    }
    if (this.resource_dice_size !== "") {
      str += ` + {resource=${ this.resource_dice_size }} Dice Size`;
    }
    if (this.resource_dice_count !== "") {
      str += ` + {resource=${ this.resource_dice_count }} Dice Count`;
    }
    if (this.selectable_key !== "") {
      str += ` + (Selectable Key = ${this.selectable_key})`;
    }
    if (this.min !== null) {
      str += ` | (Min=${ this.min })`;
    }
    if (this.max !== null) {
      str += ` | (Max=${ this.max })`;
    }
    if (str.length > 0) {
      str += ` | (Rounding=${ this.rounding })`;
    }
    return str;
  }

  getReplacerString = () => { // We can ignore some things.
    let str = this.base === 0 ? "" : `${this.base}`;
    // if (this.add_char_level_mult !== 0) { // These are always in things where we don't do replacer strings
    //   str += `+${ this.add_char_level_mult }*`;
    // }
    if (this.add_spell_save_dc_mult !== 0) { 
      str += `,{cfs.spell_dc}`; // UGNs support multiplying by it, but we never do anything but 0 or 1
    }
    if (this.add_spell_attack_mult !== 0) {
      str += `,{cfs.spell_attack}`;
    }
    if (this.add_non_magic_attack_mult !== 0) {
      str += `,{cfs.non_magic_attack}`; // UGNs support multiplying by it, but we never do anything but 0 or 1
    }
    if (this.add_magic_dmg_bonus_mult !== 0) {
      str += `,{cfs.magic_dmg_bonus}`; // UGNs support multiplying by it, but we never do anything but 0 or 1
    }
    if (this.add_non_magic_dmg_bonus_mult !== 0) {
      str += `,{cfs.non_magic_dmg_bonus}`; // UGNs support multiplying by it, but we never do anything but 0 or 1
    }
    if (this.add_prof_bonus_mult !== 0) {
      str += `,{cfs.proficiciency_modifier}`; // UGNs support multiplying by it, but we never do anything but 0 or 1
    }
    // if (this.add_ability_mod_mult !== 0) {
    //   str += `,{cfs.current_ability_scores.${this.add_ability_scores[0]}}`; // UGNs support multiplying by it, but we never do anything but 0 or 1
    // }
    
    if (this.add_stats_groups.length > 0) {
      for (const group of this.add_stats_groups) {
        str += `,{cfs`;
        if (group.mod_or_score === "Mod") {
          if (group.multiple_type === "And") {
            str += `.MOD([${group.add_ability_scores.join(",")}])`;
          } else {
            str += `.MAX_MOD([${group.add_ability_scores.join(",")}])`;
          }
        } else {
          if (group.multiple_type === "And") {
            str += `.SCORE([${group.add_ability_scores.join(",")}])`;
          } else {
            str += `.MAX_SCORE([${group.add_ability_scores.join(",")}])`;
          }
        }
        if (group.add_mult > 1) {
          str += `x${group.add_mult}`;
        } 
        str += "}";
      }
    } 
    if (str.startsWith(",")) {
      str = str.substring(1);
    }
    if (str.includes("+")) {
      str = `{sum(${str})}`;
    }
    return str;
  }
}

export interface UpgradableNumberValueProps {
  obj?: Character | BasicCreatureInstance | SummonInstance | CharacterSheetInterface;
  source_id?: string;
  root_id?: string;
  item_id?: string;
  ancestries?: FeatureAncestry[];
  base_slot_level?: number;
  slot_level?: number;
  no_upgradable_hash?: boolean;
  attack_mod?: number;
  action?: CharacterAction;
  log_it?: boolean;
}

export interface IStringUpgradableNumberHash {
  [details: string] : UpgradableNumber;
} 

export interface AddStatsUGN {
  true_id: string;
  add_ability_scores: AbilityScoreExpandedType[];
  add_mult: number;
  mod_or_score: "Mod" | "Score";
  multiple_type: "Or" | "And";
}